import { Links, PageDocs } from "./constants"

function cleanPath(path, params) {
  for (const key in params) {
    if (!params[key]) {
      delete params[key];
    }
  }
  const queryParams = new URLSearchParams(params)
  const searchString = queryParams.toString()
  if (searchString) {
    return `${path}?${searchString}`
  }
  return path
}

export function socialLoginLink(platform, redirect) {
  return cleanPath(`/social-login/${platform}`, { redirect })
}

// The share target and scheduledAt prefill a variation of the post, and the editor returns to returnTo after saving
export function savePostPagePath(postID, companyPostID, type, orgID, socialListeningPostID, { targetType, targetID, scheduledAt, returnTo } = {}) {
  const path = postID ? `/admin/posts/${postID}/edit` : `/admin/posts/new`
  return cleanPath(path, { orgID, companyPostID, type, socialListeningPostID, targetType, targetID, scheduledAt, returnTo })
}

// date is a yyyy-MM-dd day in the month or week shown
export function adminCalendarPagePath(orgID, view, date) {
  return cleanPath(Links.AdminCalendarPagePath, { orgID, view, date })
}

// date is a yyyy-MM-dd day in the month or week shown
export function advocateCalendarPagePath(orgID, view, date) {
  return cleanPath(Links.AdvocateCalendarPagePath, { orgID, view, date })
}

// sharedCalendarPagePath is the calendar of another advocate or a company page shared with the advocate
export function sharedCalendarPagePath(calendar, orgID, view, date) {
  return cleanPath(`/calendars/${calendar.Type}/${calendar.ID}`, { orgID, view, date })
}

export function adminCommentsPagePath(orgID, status) {
  return cleanPath("/admin/comments", { orgID, status })
}

export function saveCommentPagePath(commentID, orgID, socialListeningPostID) {
  const path = commentID ? `/admin/comments/${commentID}/edit` : `/admin/comments/new`
  return cleanPath(path, { orgID, socialListeningPostID })
}

export function sharePostPageLink(postID, orgID) {
  return cleanPath(`/posts/${postID}/share`, { orgID })
}

export function approvePostPageLink(postID, orgID) {
  return cleanPath(`/posts/${postID}/approve`, { orgID })
}

export function publicCampaignPagePath(campaignToken, tab) {
  return cleanPath(`/c/${campaignToken}`, { tab })
}

export function advocatePostsPagePath(orgID) {
  return cleanPath("/posts", { orgID })
}

export function advocatePendingApprovalPostsPageLink(orgID) {
  return cleanPath("/posts?status=PENDING_APPROVAL", { orgID })
}

export function advocateScheduledPostsPageLink(orgID) {
  return cleanPath("/posts?status=SCHEDULED", { orgID })
}

export function advocateSharedPostsPageLink(orgID) {
  return cleanPath("/posts?status=SHARED", { orgID })
}

export function advocateNewPostsPageLink(orgID) {
  return cleanPath("/posts?status=NEW", { orgID })
}

export function socialMediaShareDetailsPageLink(shareID, orgID) {
  return cleanPath(`/social-media-shares/${shareID}`, { orgID })
}

export function adminAnalyticsPagePath(start, end, orgID) {
  return cleanPath(`/admin/analytics`, { start, end, orgID })
}

export function adminPostsPagePath(orgID) {
  return cleanPath("/admin/posts", { orgID })
}

export function adminPostPagePath(postID, orgID) {
  return cleanPath(`/admin/posts/${postID}`, { orgID })
}

export function adminScheduledPostsPageLink(orgID) {
  return cleanPath("/admin/posts?status=SCHEDULED", { orgID })
}

export function adminDraftsPageLink(orgID) {
  return cleanPath("/admin/posts?status=DRAFT", { orgID })
}

export function adminInspirationHubPagePath(orgID, source) {
  return cleanPath(Links.AdminInspirationHubPagePath, { orgID, source })
}

export function adminSettingsPagePath(orgID) {
  return cleanPath("/admin/settings", { orgID })
}

export function advocateSettingsPagePath(orgID) {
  return cleanPath("/settings", { orgID })
}

export function generateContentOptionsApiLink({ orgID, url, content, tone, length, count, instructions, advocateID, skipBrandVoice, template }) {
  return cleanPath("/api/content/generate", { orgID, Url: url, Content: content, Tone: tone, Length: length, Count: count, Instructions: instructions, AdvocateID: advocateID, SkipBrandVoice: skipBrandVoice, Template: template })
}

export function suggestTemplatesApiLink({ orgID }) {
  return cleanPath("/api/admin/content/suggest-templates", { orgID })
}

export function rewriteApiLink(postContent) {
  return cleanPath("/api/content/rewrite", { PostContent: postContent })
}

export function generateImageApiLink({ orgID }) {
  return cleanPath("/api/admin/content/generate-image", { orgID })
}

export function rewriteSelectionApiLink({ orgID, selection, postContent, action, instructions }) {
  return cleanPath("/api/admin/content/rewrite-selection", { orgID, Selection: selection, PostContent: postContent, Action: action, Instructions: instructions })
}

// logoutLink logs out and redirects to the given relative path
export function logoutLink(redirect) {
  return cleanPath(Links.logoutPath, { redirect })
}

export function signInPageLink(redirect) {
  return cleanPath("/sign-in", { redirect })
}

export function signInLink(identityProvider, email, redirect) {
  return cleanPath(`/auth/${identityProvider}`, { email: encodeURIComponent(email), redirect })
}

export function fetchSocialMediaSharesForAdminListingAPIPath(postIDs, postVariationIDs, orgID, limit, offset) {
  return cleanPath("/api/admin/social-media-shares", { postIDs: postIDs?.join(","), postVariationIDs: postVariationIDs?.join(","), orgID, limit, offset })
}

export function fetchAdvocatesAPIPath(orgID, advocateIDs, IncludeSocialAccounts, limit, offset) {
  return cleanPath("/api/admin/advocates", { orgID, advocateIDs, IncludeSocialAccounts, limit, offset })
}

export function fetchAnalyticsTimeSeriesAPIPath({ postIDs, shareIDs, orgID, metric, currentTimePeriodStart, currentTimePeriodEnd, previousTimePeriodStart, previousTimePeriodEnd }) {
  return cleanPath(`/api/admin/analytics/time-series/${metric}`, {
    PostIDs: postIDs,
    SocialMediaShareIDs: shareIDs,
    orgID,
    CurrentTimePeriodStart: currentTimePeriodStart,
    CurrentTimePeriodEnd: currentTimePeriodEnd,
    PreviousTimePeriodStart: previousTimePeriodStart,
    PreviousTimePeriodEnd: previousTimePeriodEnd
  })
}

export function fetchAnalyticsCountsAPIPath({ shareIDs, orgID, timePeriodStart, timePeriodEnd }) {
  return cleanPath(`/api/admin/analytics/counts`, {
    SocialMediaShareIDs: shareIDs,
    orgID,
    TimePeriodStart: timePeriodStart,
    TimePeriodEnd: timePeriodEnd,
  })
}

export function generatePersonalBrandVoiceAPIPath({ orgID }) {
  return cleanPath("/api/advocate/personal-brand-voice", { orgID })
}

export function fetchLeaderboardAPIPath({ orgID, metric, limit, offset, timePeriodStart, timePeriodEnd }) {
  return cleanPath("/api/leaderboard", { orgID, Metric: metric, Limit: limit, Offset: offset, TimePeriodStart: timePeriodStart, TimePeriodEnd: timePeriodEnd })
}

export function fetchMyShareAnalyticsAPIPath({ orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd }) {
  return cleanPath("/api/me/social-media-shares/analytics", { orgID, CurrentTimePeriodStart, CurrentTimePeriodEnd, PreviousTimePeriodStart, PreviousTimePeriodEnd })
}

export function fetchMyLeaderboardPositionAPIPath({ orgID, metric, limit, timePeriodStart, timePeriodEnd }) {
  return cleanPath("/api/leaderboard/me", { orgID, Metric: metric, Limit: limit, TimePeriodStart: timePeriodStart, TimePeriodEnd: timePeriodEnd })
}

export function fetchMyTopSharesAPIPath({ orgID, limit, offset, sortOrder, timePeriodStart, timePeriodEnd }) {
  const sortOrderParam = sortOrder ? {
    "SortOrder.Field": sortOrder.Field,
    "SortOrder.Direction": sortOrder.Direction
  } : null

  return cleanPath("/api/me/analytics/social-media-shares/top", {
    orgID,
    Limit: limit,
    Offset: offset,
    TimePeriodStart: timePeriodStart,
    TimePeriodEnd: timePeriodEnd,
    ...sortOrderParam
  })
}

export function docsPageURL(slug, anchor) {
  return `${Links.DocsURL}${slug}/${anchor ? `#${anchor}` : ""}`
}

export function pageDocsLink(pathname, search) {
  const pageDoc = PageDocs.find(doc => doc.pathPattern.test(pathname) && (!doc.searchPattern || doc.searchPattern.test(search)))
  if (!pageDoc) {
    return null
  }
  return { title: pageDoc.title, url: docsPageURL(pageDoc.slug, pageDoc.anchor) }
}
